import React from 'react';

import classNames from 'classnames';

import { Figure, tdClass, UnallocatedFigure } from './cells';
import { money, percent, ZERO_DASH } from './formatting';
import LineCell from './LineCell';

const ROW_BACKGROUND = ['tw-bg-white', 'tw-bg-background/40', 'tw-bg-background/40', 'tw-bg-background/40'];

const figureOrDash = (value, any) => (any && Math.abs(value) > 1e-9 ? <Figure value={money(value)} /> : <Figure value={ZERO_DASH} dash />);

const SummaryRow = React.memo(function SummaryRow({ row, figures, columns, open, onToggle }) {
    return (
        <tr className={ROW_BACKGROUND[row.depth]}>
            <LineCell row={row} open={open} onToggle={onToggle} />
            <td className={tdClass}>
                <Figure value={money(figures.budget)} />
            </td>
            {columns.map((column) => {
                const cell = figures.columns[column.id];

                return (
                    <React.Fragment key={column.id}>
                        <td className={tdClass}>
                            {cell.any ? (
                                <Figure value={money(cell.value)} sub={cell.pct === null ? null : percent(cell.pct)} />
                            ) : (
                                <Figure value={ZERO_DASH} dash />
                            )}
                        </td>
                        <td className={tdClass}>{figureOrDash(cell.committed, cell.any)}</td>
                        <td className={tdClass}>{figureOrDash(cell.actuals, cell.any)}</td>
                    </React.Fragment>
                );
            })}
            <td className={tdClass}>
                <UnallocatedFigure figures={figures} />
            </td>
        </tr>
    );
});

/**
 * The Summary view: per column its Budget, Committed and Actuals share, grouped under
 * the column name, then the unallocated budget. Same rows and order as Allocate.
 */
export default function SummaryTable({ rows, figures, columns, expanded, onToggle }) {
    const headClass = 'tw-border-b tw-px-3 tw-py-2.5 tw-text-end tw-font-medium tw-text-charcoal tw-whitespace-nowrap';
    const total = figures.total;

    return (
        <div className="tw-overflow-x-auto tw-px-5 tw-pb-5 tw-pt-3">
            <table className="tw-w-full tw-min-w-[980px] tw-border-separate tw-border-spacing-0 tw-text-0.5sm">
                <thead>
                    <tr>
                        <th className="tw-rounded-ss-lg tw-border-b tw-border-white tw-bg-blue-10" />
                        <th className="tw-border-b tw-border-white tw-bg-blue-10" />
                        {columns.map((column) => (
                            <th
                                key={column.id}
                                colSpan={3}
                                className="tw-border-b tw-border-s tw-border-white tw-bg-blue-25 tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-charcoal"
                            >
                                <span className="tw-whitespace-pre">{column.name}</span>
                            </th>
                        ))}
                        <th className="tw-rounded-se-lg tw-border-b tw-border-s tw-border-white tw-bg-charcoal-15 tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-charcoal">
                            {$t('Unallocated')}
                        </th>
                    </tr>
                    <tr>
                        <th className={classNames(headClass, '!tw-text-start tw-border-blue-25 tw-bg-blue-10')}>{$t('Line')}</th>
                        <th className={classNames(headClass, 'tw-border-blue-25 tw-bg-blue-10')}>{$t('Budget value')}</th>
                        {columns.map((column) => (
                            <React.Fragment key={column.id}>
                                <th className={classNames(headClass, 'tw-border-blue-25 tw-bg-blue-10')}>{$t('Budget')}</th>
                                <th className={classNames(headClass, 'tw-border-blue-25 tw-bg-blue-10')}>{$t('Committed')}</th>
                                <th className={classNames(headClass, 'tw-border-blue-25 tw-bg-blue-10')}>{$t('Actuals')}</th>
                            </React.Fragment>
                        ))}
                        <th className={classNames(headClass, 'tw-border-charcoal-25 tw-bg-charcoal-10')}>{$t('Budget')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <SummaryRow key={row.key} row={row} figures={figures[row.key]} columns={columns} open={expanded.has(row.key)} onToggle={onToggle} />
                    ))}
                    <tr className="tw-bg-background tw-font-semibold">
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2 tw-text-start">{$t('Total budget')}</td>
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                            <Figure value={money(total.budget)} />
                        </td>
                        {columns.map((column) => {
                            const cell = total.columns[column.id];

                            return (
                                <React.Fragment key={column.id}>
                                    <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                                        <Figure value={money(cell.value)} />
                                    </td>
                                    <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                                        <Figure value={money(cell.committed)} />
                                    </td>
                                    <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                                        <Figure value={money(cell.actuals)} />
                                    </td>
                                </React.Fragment>
                            );
                        })}
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-p-0 tw-text-end">
                            <UnallocatedFigure figures={total} showShare={false} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
